import { Button, Dialog, DialogProps, FormLayout, Heading, Paragraph, useToast } from '@expressable/syntax-ds';
import { yupResolver } from '@hookform/resolvers/yup';
import { FC, useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCognitoUserContext } from '../AWSCognitoWrapper/context';
import { LoginFormData } from '../LoginForm';
import { getLocalizedSchema } from '../LoginForm/schema';
import { createPortal } from 'react-dom';
import { LoginFormUi } from '../LoginForm/formUi';
import { useTrack } from 'utils/hooks/useMixPanel';
import { DIRECT_SIGNUP_MIXPANEL_EVENTS } from 'domains/signupDirect/misc/mixpanelTracking';
import { HeadingCombo } from 'domains/signupDirect/SyntaxComps/HeadingCombo';
import { PasswordReset } from '../PasswordReset';
import config from 'config';

export const SignupLoginModal: FC<DialogProps> = ({ ...dialogProps }) => {
  const { isLoggedIn } = useCognitoUserContext();
  const [isResetFlow, setIsResetFlow] = useState(false);

  const cognitoCtx = useCognitoUserContext();
  if (cognitoCtx.isLoggedIn) {
    throw new Error(`<Login/> component shouldn't render if there's an active session`);
  }

  const { logIn: onLogIn } = cognitoCtx;

  const { t } = useTranslation();

  const { track } = useTrack();

  const methods = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(getLocalizedSchema(t)),
  });

  const { showToast } = useToast();

  const {
    formState: { isSubmitting: isLoading },
    handleSubmit,
    setError,
  } = methods;

  const onSuccessfulLogin = useCallback(() => {
    showToast({ variant: 'success', message: t('directSignup.loginGate.loginToast'), duration: 8000 });
    track(DIRECT_SIGNUP_MIXPANEL_EVENTS.CONTACT_CHECKER.TRIGGERED_LOGIN.SUCCESS);
  }, [showToast, track, t]);

  const onSubmit = async ({ email, password }: LoginFormData) => {
    try {
      await onLogIn(email, password);
      onSuccessfulLogin();
    } catch {
      track(DIRECT_SIGNUP_MIXPANEL_EVENTS.CONTACT_CHECKER.TRIGGERED_LOGIN.FAILURE);
      setError('root', {
        message: t('login.form.error'),
      });
    }
  };
  const supportEmail = config.get('supportEmail');

  const onDismiss = useCallback(() => {
    dialogProps.onDismiss?.();
    setIsResetFlow(false);
  }, [dialogProps]);

  if (isLoggedIn) {
    return null;
  }

  return createPortal(
    <Dialog
      {...dialogProps}
      isDismissible={dialogProps.isDismissible && !isLoading}
      onDismiss={onDismiss}
      className="bg-cream-50"
      variant="modal"
    >
      <div className="p-e32 pt-0 gap-e32 flex flex-col">
        {!isResetFlow && (
          <FormProvider {...methods}>
            <HeadingCombo>
              <Heading level="h3" noDefaultMargins>
                {t('directSignup.loginGate.loginModal.title')}
              </Heading>
              <Paragraph size="lg" variant="primary">
                {t('directSignup.loginGate.loginModal.subtitle.1')}
                <Button
                  onClick={() => window.open(`mailto:${supportEmail}`, '_blank')}
                  disabled={isLoading}
                  variant="link"
                  className="font-normal mobile-d:w-max"
                >
                  {t('directSignup.loginGate.loginModal.subtitle.2')}
                </Button>
              </Paragraph>
            </HeadingCombo>
            <FormLayout
              onSubmit={e => {
                e.stopPropagation();
                handleSubmit(onSubmit)(e);
              }}
            >
              <LoginFormUi isLoading={isLoading} signupVariant onForgotPassword={() => setIsResetFlow(true)} />
            </FormLayout>
          </FormProvider>
        )}

        {isResetFlow && (
          <PasswordReset onBack={() => setIsResetFlow(false)} onSuccessfulReset={onSubmit}>
            {({ email }) => (
              <HeadingCombo>
                <Heading level="h3" noDefaultMargins>
                  {!email ? t('login.passwordReset.pageHeading') : t('login.passwordReset.pageHeading')}
                </Heading>
                <Paragraph size="lg" variant="primary">
                  {!email ? t('login.passwordReset.step1.subTitle') : t('login.passwordReset.step2.subTitle')}
                </Paragraph>
              </HeadingCombo>
            )}
          </PasswordReset>
        )}
      </div>
    </Dialog>,
    document.body,
  );
};
